import useBackdrop from '@common/components/backdrop/useBackdrop';
import Headline from '@common/components/headline/Headline';
import {
  useGetNotificationSettingsQuery,
  useSaveNotificationSettingsMutation,
} from '@features/notifications/api/notificationsApi';
import { NotificationSettings as Settings, NotificationTrigger } from '@features/notifications/api/notificationsApi.type';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const GROUPS: { title: string, items: { trigger: NotificationTrigger, label: string }[] }[] = [
  {
    title: 'NOTIFICATIONS_GROUP_TICKETS',
    items: [
      { trigger: 'ticket_win', label: 'NOTIFICATIONS_TICKET_WIN' },
      { trigger: 'ticket_loss', label: 'NOTIFICATIONS_TICKET_LOSS' },
      { trigger: 'ticket_refund', label: 'NOTIFICATIONS_TICKET_REFUND' },
    ],
  },
  {
    title: 'NOTIFICATIONS_GROUP_FAVORITES',
    items: [
      { trigger: 'fav_score', label: 'NOTIFICATIONS_FAV_SCORE' },
      { trigger: 'fav_finished', label: 'NOTIFICATIONS_FAV_FINISHED' },
      { trigger: 'fav_started', label: 'NOTIFICATIONS_FAV_STARTED' },
    ],
  },
  {
    title: 'NOTIFICATIONS_GROUP_TRANSACTIONS',
    items: [
      { trigger: 'tx_deposit', label: 'NOTIFICATIONS_TX_DEPOSIT' },
      { trigger: 'tx_withdrawal', label: 'NOTIFICATIONS_TX_WITHDRAWAL' },
    ],
  },
];

const EMPTY: Settings = {
  ticket_win: false,
  ticket_loss: false,
  ticket_refund: false,
  fav_score: false,
  fav_finished: false,
  fav_started: false,
  tx_deposit: false,
  tx_withdrawal: false,
};

export default function NotificationSettings() {
  const backdrop = useBackdrop();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isFetching } = useGetNotificationSettingsQuery(undefined, { refetchOnMountOrArgChange: true });
  const [ saveSettings, { isLoading: isSaving } ] = useSaveNotificationSettingsMutation();
  const [ values, setValues ] = useState<Settings>(EMPTY);

  useEffect(() => {
    if (data) {
      setValues({ ...EMPTY, ...data });
    }
  }, [ data ]);

  useEffect(() => {
    backdrop(isLoading);
  }, [ backdrop, isLoading ]);

  const handleToggle = useCallback((trigger: NotificationTrigger) => {
    setValues((current) => ({ ...current, [trigger]: !current[trigger] }));
  }, []);

  const handleSave = useCallback(() => {
    saveSettings(values).unwrap().then((saved) => {
      setValues({ ...EMPTY, ...saved });
      enqueueSnackbar(<FormattedMessage id="NOTIFICATIONS_SAVED"/>, { variant: 'success' });
    }).catch(() => {
      enqueueSnackbar(<FormattedMessage id="NOTIFICATIONS_SAVE_FAILED"/>, { variant: 'error' });
    });
  }, [ enqueueSnackbar, saveSettings, values ]);

  return (
    <Fragment>
      <Headline title={<FormattedMessage id="NOTIFICATIONS_TITLE"/>} textAlign="center"/>
      <Box p={1}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          {GROUPS.map((group, index) => (
            <Fragment key={group.title}>
              {index > 0 && <Divider sx={{ my: 1.5 }}/>}
              <Typography variant="subtitle1" fontWeight="bold">
                <FormattedMessage id={group.title}/>
              </Typography>
              <Stack>
                {group.items.map((item) => (
                  <FormControlLabel
                    key={item.trigger}
                    labelPlacement="start"
                    sx={{ justifyContent: 'space-between', ml: 0, mr: 0 }}
                    label={(
                      <Typography variant="body2" sx={{ pl: 1 }}>
                        <FormattedMessage id={item.label}/>
                      </Typography>
                    )}
                    control={(
                      <Checkbox
                        color="success"
                        checked={values[item.trigger]}
                        onChange={() => handleToggle(item.trigger)}
                      />
                    )}
                  />
                ))}
              </Stack>
            </Fragment>
          ))}

          <LoadingButton
            fullWidth
            variant="contained"
            color="success"
            loading={isSaving}
            disabled={isFetching}
            onClick={handleSave}
            sx={{ mt: 2 }}
          >
            <FormattedMessage id="SAVE"/>
          </LoadingButton>
        </Paper>
      </Box>
    </Fragment>
  );
}
